import React, { useEffect, useState } from "react";
import { Loader2, Eye, Pencil, Trash2, Search } from "lucide-react";
import {
  useDeleteEmailTemplateMutation,
  useLazyGetEmailTemplateLookupsQuery,
  useLazyGetEmailTemplatesQuery,
  usePreviewEmailTemplateMutation,
} from "../../../redux/actions/Admin/ServiceAdmin";

import { IEmailTemplate } from "../../../types/Request/AdminRequest";

import EmailTemplatePreview from "../../../components/Admin/EmailTemplates/EmailTemplatePreview";

import { useNavigate } from "react-router-dom";

const EmailTemplatesScreen = () => {
  const navigate = useNavigate();

  // --------------------------------------------------
  // Email Templates
  // --------------------------------------------------

  const [getEmailTemplates, { data, isLoading, isFetching }] =
    useLazyGetEmailTemplatesQuery();

  // --------------------------------------------------
  // Email Template Lookups
  // --------------------------------------------------

  const [
    getEmailTemplateLookups,
    { data: lookupData, isLoading: isLookupLoading, isError: isLookupError },
  ] = useLazyGetEmailTemplateLookupsQuery();

  const categories = lookupData?.categories ?? [];
  const layouts = lookupData?.layouts ?? [];

  // --------------------------------------------------
  // Delete
  // --------------------------------------------------

  const [deleteEmailTemplate, { isLoading: isDeleting }] =
    useDeleteEmailTemplateMutation();

  // --------------------------------------------------
  // Preview
  // --------------------------------------------------

  const [previewTemplate, { isLoading: isPreviewing }] =
    usePreviewEmailTemplateMutation();

  // --------------------------------------------------
  // State
  // --------------------------------------------------

  const [templates, setTemplates] = useState<IEmailTemplate[]>([]);

  const [search, setSearch] = useState("");

  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);

  const [previewHtml, setPreviewHtml] = useState("");

  const [previewSubject, setPreviewSubject] = useState("");

  // --------------------------------------------------
  // Load
  // --------------------------------------------------

  useEffect(() => {
    getEmailTemplates();
    getEmailTemplateLookups();
  }, []);

  // --------------------------------------------------
  // Sync templates
  // --------------------------------------------------

  useEffect(() => {
    if (data) {
      setTemplates(data);
    }
  }, [data]);

  // --------------------------------------------------
  // Filter
  // --------------------------------------------------

  const filteredTemplates = templates.filter((item) => {
    const value = search.trim().toLowerCase();

    const categoryName =
      categories.find((category) => category.id === item.emailCategoryId)
        ?.name ?? "";

    const layoutName =
      layouts.find((layout) => layout.id === item.emailLayoutId)?.name ?? "";

    return (
      (item.name ?? "").toLowerCase().includes(value) ||
      (item.templateKey ?? "").toLowerCase().includes(value) ||
      (item.subject ?? "").toLowerCase().includes(value) ||
      categoryName.toLowerCase().includes(value) ||
      layoutName.toLowerCase().includes(value)
    );
  });

  // --------------------------------------------------
  // Delete
  // --------------------------------------------------

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await deleteEmailTemplate(deleteId).unwrap();

      setShowDeleteDialog(false);
      setDeleteId(null);

      getEmailTemplates();
    } catch (error) {
      console.error(error);
    }
  };

  // --------------------------------------------------
  // Preview
  // --------------------------------------------------

  const handlePreview = async (item: IEmailTemplate) => {
    try {
      const result = await previewTemplate({
        templateKey: item.templateKey,

        variables: {
          CustomerName: "Rushikesh",
          OrderNumber: "PL-10025",
          Difference: "AED 150",
          PaymentUrl: "https://petland.com/payment",
        },
      }).unwrap();

      console.log("PREVIEW RESPONSE:", result);

      setPreviewSubject(result.subject ?? "");

      setPreviewHtml((result.htmlBody ?? "").replace(/\r\n/g, ""));

      setPreviewOpen(true);
    } catch (error) {
      console.error("Preview Error:", error);
    }
  };

  // --------------------------------------------------
  // Loading
  // --------------------------------------------------

  if (isLoading || isFetching) {
    return (
      <div className="flex min-h-[400px] items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="animate-spin text-primary" size={32} />

          <p className="text-sm text-slate-500">Loading email templates...</p>
        </div>
      </div>
    );
  }

  // --------------------------------------------------
  // UI
  // --------------------------------------------------

  return (
    <>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {/* ====================================================== */}
        {/* Section Header */}
        {/* ====================================================== */}

        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Email Templates
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                Create and manage email templates used across the system.
              </p>
            </div>

            {/* Search */}
            <div className="relative w-full lg:w-80">
              <Search
                size={17}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="text"
                placeholder="Search templates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-4 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/10"
              />
            </div>
          </div>
        </div>

        {/* ====================================================== */}
        {/* Lookup Warning */}
        {/* ====================================================== */}

        {isLookupError && (
          <div className="mx-5 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            Unable to load email category and layout information.
          </div>
        )}

        {/* ====================================================== */}
        {/* Table */}
        {/* ====================================================== */}

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Name
                </th>

                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Category
                </th>

                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Layout
                </th>

                <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Type
                </th>

                <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Status
                </th>

                <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredTemplates.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                        <Search size={20} className="text-slate-400" />
                      </div>

                      <p className="mt-3 text-sm font-medium text-slate-700">
                        No email templates found
                      </p>

                      <p className="mt-1 text-xs text-slate-400">
                        Try changing your search criteria.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredTemplates.map((item) => {
                  const categoryName =
                    categories.find(
                      (category) => category.id === item.emailCategoryId,
                    )?.name ?? "-";

                  const layoutName =
                    layouts.find((layout) => layout.id === item.emailLayoutId)
                      ?.name ?? "-";

                  return (
                    <tr
                      key={item.id}
                      className="border-b border-slate-100 transition hover:bg-slate-50/70"
                    >
                      {/* Name */}
                      <td className="px-5 py-3.5">
                        <div>
                          <p className="text-sm font-medium text-slate-800">
                            {item.name}
                          </p>

                          <p className="mt-0.5 text-xs text-slate-400">
                            {item.templateKey}
                          </p>
                        </div>
                      </td>

                      {/* Category */}
                      <td className="px-5 py-3.5">
                        <span className="text-sm text-slate-600">
                          {categoryName}
                        </span>
                      </td>

                      {/* Layout */}
                      <td className="px-5 py-3.5">
                        <span className="text-sm text-slate-600">
                          {layoutName}
                        </span>
                      </td>

                      {/* Type */}
                      <td className="px-5 py-3.5 text-center">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                            item.isSystem
                              ? "bg-slate-100 text-slate-600"
                              : "bg-blue-50 text-blue-600"
                          }`}
                        >
                          {item.isSystem ? "System" : "Custom"}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-5 py-3.5 text-center">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                            item.isActive
                              ? "bg-green-50 text-green-600"
                              : "bg-red-50 text-red-600"
                          }`}
                        >
                          {item.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-center gap-3">
                          {/* Preview */}
                          <button
                            type="button"
                            title="Preview"
                            disabled={isPreviewing}
                            onClick={() => handlePreview(item)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-blue-600 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {isPreviewing ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Eye size={17} />
                            )}
                          </button>

                          {/* Edit */}
                          <button
                            type="button"
                            title="Edit"
                            onClick={() => {
                              navigate(
                                `/admin/adminEmailTemplates/edit/${item.id}`,
                              );
                            }}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-green-600 transition hover:bg-green-50"
                          >
                            <Pencil size={17} />
                          </button>

                          {/* Delete only custom */}
                          {!item.isSystem && (
                            <button
                              type="button"
                              title="Delete"
                              onClick={() => {
                                setDeleteId(item.id);
                                setShowDeleteDialog(true);
                              }}
                              className="flex h-8 w-8 items-center justify-center rounded-lg text-red-600 transition hover:bg-red-50"
                            >
                              <Trash2 size={17} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ====================================================== */}
        {/* Footer */}
        {/* ====================================================== */}

        <div className="border-t border-slate-200 bg-slate-50/50 px-5 py-3">
          <p className="text-xs text-slate-500">
            Showing{" "}
            <span className="font-semibold text-slate-700">
              {filteredTemplates.length}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-slate-700">
              {templates.length}
            </span>{" "}
            email templates
          </p>
        </div>
      </div>

      {/* ======================================================== */}
      {/* Preview */}
      {/* ======================================================== */}

      <EmailTemplatePreview
        open={previewOpen}
        html={previewHtml}
        subject={previewSubject}
        onClose={() => setPreviewOpen(false)}
      />

      {/* ======================================================== */}
      {/* Delete Dialog */}
      {/* ======================================================== */}

      {showDeleteDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-[420px] rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-slate-900">
              Delete Email Template
            </h2>

            <p className="mt-3 text-sm leading-6 text-slate-500">
              Are you sure you want to delete this email template?
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeleteId(null);
                }}
                className="rounded-lg border border-slate-200 bg-white px-5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isDeleting && <Loader2 size={15} className="animate-spin" />}

                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmailTemplatesScreen;
